import { Trans, useLingui } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import type { Visit } from '../../../../@types/visits';
import { useGetRecentVisits } from '../../../../react-query/useGetRecentVisits';

import {
  selectedAdjustmentTypeAtom,
  selectedImageIdAtom,
  selectedPatientIdAtom,
  selectedVisitAtom,
} from './atoms';

export const VisitSelector = () => {
  const { t } = useLingui();
  const [selectedVisit, setSelectedVisit] = useAtom(selectedVisitAtom);
  const setSelectedPatientId = useSetAtom(selectedPatientIdAtom);
  const setSelectedImageId = useSetAtom(selectedImageIdAtom);
  const selectedAdjustmentType = useAtomValue(selectedAdjustmentTypeAtom);
  const imageTypes =
    selectedAdjustmentType === 'Pano'
      ? ['PANORAMIC']
      : ['BITEWING', 'PERIAPICAL'];

  const recentVisits = useGetRecentVisits(imageTypes);

  const visits = recentVisits.data?.visits || [];

  const handleVisitChange = (visitId: string) => {
    const patientId =
      visits?.find((visit) => visit.id === Number(visitId))?.patientVideaId ||
      '';
    setSelectedVisit(Number(visitId));
    setSelectedPatientId(patientId);
    setSelectedImageId(undefined);
  };

  return (
    <>
      {recentVisits.isLoading ? (
        <div className="vai-p-4 vai-text-center vai-text-gray-500">
          <Trans>Loading recent visits...</Trans>
        </div>
      ) : recentVisits.isError ? (
        <div className="vai-p-4 vai-text-center vai-text-red-600">
          <Trans>Error loading visits. Please try again.</Trans>
        </div>
      ) : (
        <Select
          key={selectedVisit ?? 'none'}
          value={selectedVisit ? selectedVisit.toString() : ''}
          onValueChange={handleVisitChange}
        >
          <SelectTrigger className="vai-w-full">
            <SelectValue placeholder={t`Choose a visit...`} />
          </SelectTrigger>
          <SelectContent>
            {visits?.map((visit: Visit) => (
              <SelectItem key={visit.id} value={visit.id!.toString()}>
                {new Date(visit.visitDate!).toLocaleDateString()} (
                {visit.imageTypeCounts.reduce(
                  (acc, curr) =>
                    imageTypes.includes(curr.type)
                      ? acc + (Number(curr.count) ?? 0)
                      : acc,
                  0,
                )}{' '}
                images)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  );
};
